/**
 * The menu trigger (lazy-game-graph-boot-seam design) and the Escape hotkey that reaches the same
 * menu without it. Split out of render/render.js so the start screen's only edge into the game
 * graph (`game-start-ui.js` building its own instance) is cut: everything this module needs —
 * utils.js, i18n.js, icons/prompt-icons.js, state.js — is already eager, and the menu it opens is
 * reached through a dynamic import exactly like game-menu.js's own reference-overlay.js opens
 * (js/CLAUDE.md § Fragment intercept precedent). The hotkey holds that same line: it opens through
 * `openMenu` below and its guards are plain DOM reads, so it adds no import of its own.
 */
import { t } from "./i18n.js";
import { btn } from "./utils.js";
import { createMenuIcon } from "./icons/prompt-icons.js";
import { getCurrentState } from "./state.js";

/** The in-game menu trigger, pinned top-right and engine-sized/-placed (`--menu-*`).
 *  It's the lone in-game chrome control — what's left of the retired info bar,
 *  which emptied once declarer-state moved to the position strips (remove-info-bar)
 *  and the round number left gameplay (round survives on the result + game-start
 *  screens). Static — no state-dependent content — so it needs no dispatch-path
 *  canonicalizer: built once at each full render and persists.
 *
 *  quit-becomes-menu-button: this used to be the lone Quit button (instant
 *  showGameStart()); it now opens the context-aware menu overlay (game-menu.js),
 *  which carries Quit as one entry. The DOM class (`.menu-button`/`.menu-icon`)
 *  and this function's name were renamed from `quit-*` to match, once the
 *  prototype cut settled.
 *
 *  Also arms `html.in-game-chrome` on mount — the class mirror of this instance
 *  specifically (never `.menu-button-start`, buildMenuButton's other caller), so it
 *  tracks the real in-game control: skat.css keys the site-footer hide off it
 *  (phone-style-recalc-stall-class — was `body:has(.menu-button)`).
 *  game.js#clearAppForRender drops it again at the next full render's wholesale
 *  sweep. */
export function renderMenuButton(appEl) {
  appEl.appendChild(buildMenuButton("menu-button"));
  document.documentElement.classList.add("in-game-chrome");
}

/** Menu button is icon-only — glass `.icon-tile` material (the prompt-tile
 *  vocabulary), not the recessed register the old Quit button wore: it now opens
 *  an overlay rather than committing to an action, so it reads as a window into
 *  the game rather than a struck-metal control. `extraClass` distinguishes the
 *  in-game instance (`menu-button` — carries the `html.in-game-chrome`
 *  context flip, armed by `renderMenuButton` above) from the start-screen instance
 *  (`menu-button-start`, game-start-ui.js — must NOT flip that context, so it
 *  gets its own class and never arms the mirror).
 *
 *  Its click handler is `openMenu` below, shared verbatim with the Escape hotkey. */
export function buildMenuButton(extraClass) {
  const menu = btn("", `icon-tile ${extraClass}`, openMenu);
  // aria-label, not just data-tooltip: an icon-only button with no text content needs an
  // accessible name of its own — a tooltip is a description, never a name (js/tooltip.js §1 a11y).
  menu.dataset.tooltip = t("menu");
  menu.setAttribute("aria-label", t("menu"));
  const icon = createMenuIcon();
  icon.classList.add("menu-icon");
  menu.appendChild(icon);
  return menu;
}

/** Opens the menu overlay — the one path both affordances take (the button's click, the Escape
 *  hotkey below), so "how the menu opens" is stated once.
 *
 *  Dynamic-imports game-menu.js rather than statically importing it: the game graph (game.js and
 *  everything it wires) must never be reachable from this eager module, so the menu itself — and
 *  everything it in turn opens (games-overlay.js, reference-overlay.js) — loads only on demand. On
 *  a cold start screen the menu graph may still be warming (warmGameGraph()'s fetch-only preload
 *  doesn't evaluate it) — the overlay-open motion absorbs that latency, the same conditional-wait
 *  class as Play.
 *
 *  getCurrentState() reads at RESOLVE time, not click time (spec's exact shape) — the cold-menu
 *  latency window is exactly where a server response could land, so the state the menu opens
 *  against must be whatever is current when the module is actually ready, not a stale capture. */
function openMenu() {
  return import("./game-menu.js").then((m) =>
    m.openGameMenu(getCurrentState()),
  );
}

/**
 * Escape opens the menu (todo/esc-menu-hotkey) — the initiative's one constant gesture, so the
 * menu is reachable from wherever the player stands rather than only from the corner tile.
 *
 * Closing is the platform's, not ours: the overlay is a native `<dialog>` shown with `showModal()`
 * (overlay-shell.js), which already dismisses on Escape. Toggle symmetry therefore needs no close
 * path here — only the dialog guard below, which keeps that same Escape from re-opening what it
 * just closed.
 *
 * Installed once, from boot.js#init — the `initTooltips` / `initCardQueue` shape. `window`, not
 * `document`, is deliberate: it puts this listener after every document-level one however late that
 * one installs. The transients that own Escape while visible (tooltip.js#onKeyDown,
 * result/trick-chunk-tip.js#onEscape) consume it with `preventDefault` + `stopPropagation` on
 * `document`, and stopPropagation is what keeps the event from reaching `window` at all — whereas a
 * sibling `document` listener here would run first for trick-chunk-tip, which installs lazily with
 * the result graph, and open the menu over a visible tip.
 */
let hotkeyInstalled = false;
export function initMenuHotkey() {
  if (hotkeyInstalled) return;
  hotkeyInstalled = true;
  window.addEventListener("keydown", onEscape);
}

function onEscape(e) {
  if (e.key !== "Escape") return;
  // A transient consumed it (the stopPropagation above is the usual stop; this covers a consumer
  // that only preventDefaults). `defaultPrevented`, not a module flag — it reads every consumer.
  if (e.defaultPrevented) return;
  // Fullscreen is the outermost layer of tooltip.js's "topmost transient consumes the Escape, so
  // the NEXT one reaches an enclosing dialog" doctrine — so the first Escape belongs to leaving
  // it, and a second then opens the menu. This holds regardless of whether a given browser also
  // delivers the keydown that exits fullscreen (unverified here), so the behavior is deterministic
  // either way. `fullscreenElement` is set only for the API-driven fullscreen `fullscreen.js`'s
  // own button requests — a viewer in browser-level F11 fullscreen has no `fullscreenElement` and
  // keeps the hotkey.
  if (document.fullscreenElement != null) return;
  // Any open dialog owns this Escape: the platform is closing it (overlay-shell.js's `cancel`
  // teardown, confirm-dialog.js), and the menu must not open over — or right behind — it.
  if (document.querySelector("dialog[open]") != null) return;
  // "The player is standing on a menu-bearing surface", read structurally rather than by
  // enumerating screens: a live trigger IS the surface's claim to a menu, and the two instances
  // buildMenuButton mounts (the board's, the start screen's) are exactly this hotkey's scope.
  if (document.querySelector(".menu-button, .menu-button-start") == null)
    return;
  openMenu();
}
